import PropTypes from "prop-types";
import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { makeStyles } from "tss-react/mui";

import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import FormLabel from "@mui/material/FormLabel";
import FormControlLabel from "@mui/material/FormControlLabel";
import RadioGroup from "@mui/material/RadioGroup";
import Select from "@mui/material/Select";
import Radio from "@mui/material/Radio";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";

import TextField from "@mui/material/TextField";
import { showToast } from "../../../store/actions";
import WorkspaceClass from "../../../service/workspace";
import { ButtonUI, KMKDialog } from "../../Common";
import FormHelperText from "@mui/material/FormHelperText";
import { regexAccountLocator } from "../../../util/regularExpress";
import { setPrevState } from "../../../util/common";
import PasswordInput from "../../Common/PasswordInput";

const useStyles = makeStyles()((theme) => ({
  dialogPaper: {
    "& .MuiPaper-root": {
      maxWidth: "600px",
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
    },
    "& .MuiGrid-item": {
      paddingTop: "15px",
    },
    "& .MuiGrid-item .MuiTextField-root": {
      marginTop: 0,
    },
    "& .MuiDivider-root": {
      marginTop: "5px",
      marginBottom: "9px",
    },
    "& .MuiFormControl-root": {
      margin: 0,
    },
    "& .MuiDialogActions-root": {
      marginBottom: "10px",
      width: "100%",
      paddingRight: "0px",
    },
  },
  formContainer: {
    display: "grid",
    width: "100%",
    textAlign: "left",
    minHeight: "150px",
  },
  container: {
    padding: "24px 24px 0 24px",
    backgroundColor: "#FFFFFF",
  },
  Toolbar: {
    fontSize: "1.1rem",
    backgroundColor: "white",
    color: "black",
  },
  ifDisabled: {
    "& .Mui-disabled": {
      cursor: "not-allowed",
    },
  },
}));

AddLocation.propTypes = {
  configData: PropTypes.object,
};

export default function AddLocation({ configData }) {
  const awsRegions = useSelector((state) => state.config.awsRegions);

  const [loading, setLoading] = useState(false);
  const [dataProvider, setDataProvider] = useState("S3");
  const [dataIntegrationName, setDataIntegrationName] = useState("");
  const [dataIntegrationNameError, setDataIntegrationNameError] = useState("");
  const [accessType, setAccessType] = useState("Access Key");
  const [accessKeyID, setAccessKeyID] = useState("");
  const [accessKeySecret, setAccessKeySecret] = useState("");
  const [ARN, setARN] = useState("");
  const [region, setRegion] = useState("");
  const [regionError, setRegionError] = useState("");

  const [url, setURL] = useState("");
  const [accountKey, setAccountKey] = useState("");
  const [securityKey, setSecurityKey] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const [port, setPort] = useState(22);
  const [authMethod, setAuthMethod] = useState("password");
  const [key, setKey] = useState("");

  const [version, setVersion] = useState("v24.1");
  const [veevaUrl, setVeevaUrl] = useState("");

  const [container, setContainer] = useState("");

  // For SharePoint
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [docuList, setDocuList] = useState("");
  const [docuListError, setDocuListError] = useState("");

  // For external Snowflake connection
  const [snowflakeState, setSnowflakeState] = useState({
    database: "",
    schema: "",
    warehouse: "",
  });

  const { database, schema, warehouse } = snowflakeState;

  const { Workspace_id } = useParams();
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const extractDomain = (url) => {
    let domain;
    if (url?.indexOf("://") > -1) {
      domain = url.split("/")[2];
    } else {
      domain = url.split("/")[0];
    }
    domain = domain.split(":")[0];
    return domain;
  };

  const handleSave = async () => {
    setLoading(true);
    setDataIntegrationNameError("");
    setRegionError("");
    if (dataIntegrationName.length > 15 || dataIntegrationName.length < 2)
      setDataIntegrationNameError("Name must be between 2 to 15 characters.");
    else if (dataProvider.toLowerCase() === "s3" && region === "") setRegionError("Please select region.");
    else if (
      !configData.stage &&
      configData.locationList?.length &&
      configData.locationList?.some((option) => option.Name.toLowerCase() === dataIntegrationName.toLowerCase())
    )
      setDataIntegrationNameError("Name must be unique.");
    else {
      let response = null;

      let submitData = {
        type: dataProvider,
        dataIntegrationName,
        url,
      };

      if (dataProvider.toLowerCase() === "s3")
        submitData = {
          ...submitData,
          accessType,
          accessKeyID,
          accessKeySecret,
          region,
          ARN,
        };
      else if (dataProvider.toLowerCase() === "salesforce")
        submitData = {
          ...submitData,
          securityKey,
          username,
          password,
        };
      else if (dataProvider.toLowerCase() === "sftp")
        submitData = {
          ...submitData,
          url,
          port,
          username,
          sftpAuthMethod: authMethod,
          key,
          password,
        };
      else if (dataProvider.toLowerCase() === "azure")
        submitData = {
          ...submitData,
          username,
          container,
          accountKey,
        };
      else if (dataProvider.toLowerCase() === "sharepoint")
        submitData = {
          ...submitData,
          clientId,
          clientSecret,
          docuList,
        };
      else if (dataProvider.toLowerCase() === "mysql")
        submitData = {
          ...submitData,
          url,
          port,
          username,
          password,
        };
      else if (dataProvider.toLowerCase() === "veevavault")
        submitData = {
          ...submitData,
          url: extractDomain(veevaUrl),
          version,
          username,
          password,
        };
      else if (dataProvider.toLowerCase() === "snowflake")
        submitData = {
          ...submitData,
          database,
          schema,
          warehouse,
          username,
          authMethod,
          password,
          key,
        };

      const workspace = new WorkspaceClass();
      if (!configData.stage)
        response = await workspace.addLocationData(configData.Workspace_id, submitData, dataProvider);
      else
        response = await workspace.updateLocationData(
          configData.Workspace_id,
          configData.stage,
          submitData,
          dataProvider
        );

      if (response.success) {
        configData.loadLocationList(configData.accessRole);

        dispatch(
          showToast({
            message: response.message,
            type: "success",
          })
        );

        configData.setOpen(false);
      }
    }

    setLoading(false);
  };

  const getS3RegionLocation = useCallback(
    async (event) => {
      let value = event.target.value;
      setRegionError("");
      if (value !== "") {
        setLoading(true);

        const workspace = new WorkspaceClass();
        let response = await workspace.getS3RegionLocation(configData.Workspace_id, { url: value });
        if (response.success) {
          setRegion(response.data.region);
        } else {
          setRegion("");
        }
        setLoading(false);
      }
    },
    [configData.Workspace_id]
  );

  const clearFormData = (clearName = true) => {
    if (clearName) setDataIntegrationName("");
    setARN("");
    setAccessKeyID("");
    setAccessKeySecret("");
    setURL("");
    setRegion("");
    setRegionError("");
    setSecurityKey("");
    setUsername("");
    setPassword("");
    setPort(22);
    setKey("");
    setClientId("");
    setClientSecret("");
    setDocuList("");
    setDocuListError("");
    setVersion(version);
    setVeevaUrl("");
  };

  const updateDataProvider = (targetValue) => {
    setDataProvider(targetValue);
    clearFormData(false);
    if (targetValue.toLowerCase() === "sftp") setPort(22);
    else if (targetValue.toLowerCase() === "mysql") setPort(3306);
    else setPort("");
  };

  const handleRegion = (event) => {
    setRegion(event.target.value);
    if (event.target.value !== "") setRegionError("");
  };

  const handleCancel = () => {
    configData.setOpen(false);
  };

  const validateDocuListPath = (e) => {
    const { value } = e.target;
    const shouldNotContainSpecialCharsRegex = /^[a-zA-Z0-9\s\/_]*$/;
    const notStartWithSlashRegex = /^[^\/].*$/;
    const notMoreThanOneSlashAtOnceRegex = /^(?!\/)(?!.*\/\/)[^\/].*$/;

    if (value.trim().length > 0) {
      if (!shouldNotContainSpecialCharsRegex.test(value)) {
        setDocuListError("Path cannot contain special chars other than space, slash or underscore");
      } else if (!notStartWithSlashRegex.test(value)) {
        setDocuListError("Path cannot start with slash");
      } else if (!notMoreThanOneSlashAtOnceRegex.test(value)) {
        setDocuListError("Path cannot contain double slash");
      } else {
        setDocuListError("");
      }
    } else {
      setDocuListError("");
    }
  };

  const disabledSaveBtn = () => {
    if (loading) return true;
    if (!dataIntegrationName?.trim()) return true;

    const isDataProvider = dataProvider.toLowerCase();

    const isS3 =
      isDataProvider === "s3" &&
      url?.trim() &&
      region &&
      (accessType === "Access Key" ? accessKeyID?.trim() && accessKeySecret?.trim() : ARN?.trim());

    const isSalesforce =
      isDataProvider === "salesforce" && url?.trim() && securityKey?.trim() && username?.trim() && password?.trim();

    const isSFTP = isDataProvider === "sftp" && (key?.trim() || password?.trim()) && username?.trim() && url?.trim();

    const isMySQL = isDataProvider === "mysql" && username?.trim() && port && url?.trim() && password?.trim();

    const isAzure =
      isDataProvider === "azure" &&
      (configData.stage ? accountKey?.trim() : url?.trim() && username?.trim() && container?.trim());

    const isSharepoint =
      isDataProvider === "sharepoint" &&
      (configData.stage
        ? clientId?.trim() && clientSecret?.trim()
        : url?.trim() && clientId?.trim() && clientSecret?.trim() && docuList?.trim() && docuListError.length === 0);

    const isVeevaVault =
      isDataProvider === "veevavault" && veevaUrl?.trim() && version?.trim() && username?.trim() && password?.trim();

    const isSnowflake =
      isDataProvider === "snowflake" && username.trim() && url?.trim() && (key?.trim() || password?.trim());

    const isValidProvider =
      isS3 || isSalesforce || isSFTP || isMySQL || isAzure || isSharepoint || isVeevaVault || isSnowflake;

    return !isValidProvider;
  };

  useEffect(() => {
    const loadConnectionData = async () => {
      setLoading(true);

      const workspace = new WorkspaceClass();
      const result = await workspace.getConnectionData(Workspace_id, configData.stage);

      if (result) {
        setDataProvider(result.type);
        setDataIntegrationName(result.Name);
        setURL(result.URL || "");
        setUsername(result?.username || "");
        if (result.type.toLowerCase() === "s3") {
          setAccessType(result.AccessType);
          setRegion(result.Region || "");
          setRegionError("");
          if (result.AccessType === "Access Key") setAccessKeyID(result.AccessKey);
          else setARN(result.ARN);
        } else if (result.type.toLowerCase() === "salesforce") {
          setSecurityKey(result.SecurityKey);
        } else if (result.type.toLowerCase() === "sftp") {
          setPort(result.port);
          setAuthMethod(result.sftpAuthMethod);
        } else if (result.type.toLowerCase() === "azure") {
          setContainer(result.container);
        } else if (result.type.toLowerCase() === "sharepoint") {
          setClientId(result.clientId);
          setDocuList(result.docuList);
        } else if (result.type.toLowerCase() === "mysql") {
          setPort(result.port);
        } else if (result.type.toLowerCase() === "veevavault") {
          setVeevaUrl(extractDomain(result.URL));
          setVersion(result.version);
        } else if (result.type.toLowerCase() === "snowflake") {
          setSnowflakeState({
            database: result?.database || "",
            schema: result?.schema || "",
            warehouse: result?.warehouse || "",
          });
          setAuthMethod(result.authMethod);
        }
      }
      setLoading(false);
    };

    if (configData.stage) loadConnectionData();
    return () => {
      clearFormData();
    };
  }, [Workspace_id, configData.stage]);

  const AWSS3Fields = useCallback(() => {
    return (
      <>
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <TextField
            required
            variant="outlined"
            inputProps={{ maxLength: 255 }}
            value={url}
            name="url"
            onChange={(e) => setURL(e.target.value)}
            onBlur={getS3RegionLocation}
            fullWidth
            label="URL"
            disabled={!!configData.stage}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <FormControl fullWidth required variant="outlined" sx={{ my: 2 }}>
            <InputLabel>Region</InputLabel>
            <Select error={!!regionError} value={region} onChange={handleRegion} label="Region">
              {awsRegions?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.text}
                </MenuItem>
              ))}
            </Select>
            {regionError !== "" && <FormHelperText style={{ color: "red" }}>{regionError}</FormHelperText>}
          </FormControl>
        </Grid>
        {accessType === "Access Key" ? (
          <>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                required
                variant="outlined"
                value={accessKeyID}
                name="accessKeyID"
                autoComplete="username"
                inputProps={{ maxLength: 255 }}
                onChange={(e) => setAccessKeyID(e.target.value)}
                fullWidth
                label="Access Key ID"
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <PasswordInput
                setPassword={setAccessKeySecret}
                inputProps={{ maxLength: 255 }}
                label="Access Key Secret"
                value={accessKeySecret || ""}
              />
            </Grid>
          </>
        ) : (
          <Grid size={6}>
            <TextField
              required
              variant="outlined"
              inputProps={{ maxLength: 255 }}
              value={ARN}
              onChange={(e) => setARN(e.target.value)}
              fullWidth
              label="ARN"
            />
          </Grid>
        )}
      </>
    );
  }, [
    ARN,
    accessKeyID,
    accessKeySecret,
    accessType,
    awsRegions,
    configData.stage,
    getS3RegionLocation,
    region,
    regionError,
    url,
  ]);

  const SalesforceFields = useCallback(() => {
    return (
      <>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <PasswordInput
            setPassword={setSecurityKey}
            inputProps={{ maxLength: 255 }}
            label="Security Key"
            value={securityKey || ""}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <TextField
            margin="dense"
            label="Username"
            fullWidth
            variant="outlined"
            value={username}
            autoComplete="username"
            inputProps={{ maxLength: 255 }}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <PasswordInput
            margin="dense"
            setPassword={setPassword}
            inputProps={{ maxLength: 255 }}
            value={password || ""}
          />
        </Grid>
      </>
    );
  }, [password, securityKey, username]);

  const SFTPFields = useCallback(() => {
    return (
      <>
        <Grid
          size={{
            xs: 12,
            sm: dataProvider === "mysql" ? 12 : 6
          }}>
          <Grid container spacing={1}>
            <Grid
              size={{
                sm: 8
              }}>
              <TextField
                required
                variant="outlined"
                fullWidth
                label="Host"
                inputProps={{ maxLength: 255 }}
                value={url}
                onChange={(e) => setURL(e.target.value)}
                disabled={!!configData.stage}
              />
            </Grid>
            <Grid
              size={{
                xs: 2,
                sm: 4
              }}>
              <TextField
                type="number"
                required
                variant="outlined"
                fullWidth
                label="Port"
                value={port}
                inputProps={{
                  readOnly: dataProvider === "sftp",
                }}
                onChange={(e) => setPort(e.target.value)}
                disabled={!!configData.stage}
              />
            </Grid>
          </Grid>
        </Grid>
        {dataProvider === "sftp" && (
          <Grid size={6}>
            <FormControl>
              <FormLabel>Specify Authentication Method</FormLabel>
              <RadioGroup
                row
                value={authMethod}
                aria-labelledby="demo-controlled-radio-buttons-group-2"
                name="controlled-radio-buttons-group-2"
                onChange={(e) => {
                  setAuthMethod((prevState) => {
                    prevState === "password" ? setPassword("") : setKey("");
                    return e.target.value;
                  });
                }}
                className={classes.ifDisabled}
              >
                <FormControlLabel
                  value="password"
                  control={<Radio checked={authMethod === "password"} />}
                  label="Password"
                  disabled={!!configData.stage}
                />
                <FormControlLabel
                  value="key"
                  control={<Radio checked={authMethod === "key"} />}
                  label="Key"
                  disabled={!!configData.stage}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
        )}
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <TextField
            required
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            variant="outlined"
            fullWidth
            label="Username"
            inputProps={{ maxLength: 255 }}
            autoComplete="username"
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          {dataProvider === "mysql" || authMethod === "password" ? (
            <PasswordInput
              setPassword={setPassword}
              inputProps={{ maxLength: 255 }}
              label="Password"
              value={password || ""}
              required={dataProvider === "sftp"}
            />
          ) : (
            <PasswordInput setPassword={setKey} inputProps={{ maxLength: 255 }} label="Key" value={key} />
          )}
        </Grid>
      </>
    );
  }, [dataProvider, classes.ifDisabled, configData.stage, key, password, port, authMethod, url, username]);

  const AzureFields = useCallback(() => {
    return (
      <>
        <Grid size={12}>
          {!configData.stage ? (
            <PasswordInput
              margin="dense"
              setPassword={setURL}
              inputProps={{ maxLength: 255 }}
              label="URL"
              value={url}
            />
          ) : (
            <PasswordInput
              margin="dense"
              setPassword={setAccountKey}
              inputProps={{ maxLength: 255 }}
              label="Account Key"
              value={accountKey}
            />
          )}
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <TextField
            margin="dense"
            label="AccountName"
            fullWidth
            variant="outlined"
            value={username}
            autoComplete="username"
            inputProps={{ maxLength: 255 }}
            onChange={(e) => setUsername(e.target.value)}
            required
            disabled={!!configData.stage}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <TextField
            margin="dense"
            label="Container"
            fullWidth
            variant="outlined"
            value={container}
            autoComplete="Container"
            inputProps={{ maxLength: 60 }}
            onChange={(e) => setContainer(e.target.value.toLowerCase())}
            required
            disabled={!!configData.stage}
          />
        </Grid>
      </>
    );
  }, [accountKey, configData.stage, container, url, username]);

  const SPFields = useCallback(() => {
    return (
      <>
        <Grid size={12}>
          <TextField
            required
            margin="dense"
            inputProps={{ maxLength: 255 }}
            onChange={(e) => setURL(e.target.value)}
            fullWidth
            value={url}
            label="Site URL"
            disabled={!!configData.stage}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            required
            margin="dense"
            inputProps={{ maxLength: 255 }}
            onChange={(e) => setDocuList(e.target.value)}
            fullWidth
            value={docuList}
            label="Folder Path"
            disabled={!!configData.stage}
            onBlur={validateDocuListPath}
            error={docuListError.length > 0}
            helperText={docuListError.length ? docuListError : ""}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <TextField
            margin="dense"
            label="Client ID"
            fullWidth
            variant="outlined"
            value={clientId}
            autoComplete="username"
            onChange={(e) => setClientId(e.target.value)}
            required
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <PasswordInput
            margin="dense"
            setPassword={setClientSecret}
            inputProps={{ maxLength: 255 }}
            label="Client Secret Key"
            value={clientSecret}
          />
        </Grid>
      </>
    );
  }, [clientId, clientSecret, configData.stage, docuList, docuListError, url]);

  const VeevaFields = useCallback(() => {
    return (
      <>
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <TextField
            required
            margin="dense"
            fullWidth
            inputProps={{ maxLength: 255 }}
            onChange={(e) => setVeevaUrl(e.target.value)}
            value={veevaUrl}
            label="Veeva Domain"
            disabled={!!configData.stage}
            error={docuListError.length > 0}
            helperText={docuListError.length ? docuListError : ""}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <TextField
            required
            margin="dense"
            inputProps={{ maxLength: 255 }}
            onChange={(e) => setVersion(e.target.value)}
            fullWidth
            value={version}
            label="API Version"
            error={docuListError.length > 0}
            helperText={docuListError.length ? docuListError : ""}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <TextField
            margin="dense"
            label="Username"
            fullWidth
            variant="outlined"
            value={username}
            autoComplete="username"
            onChange={(e) => setUsername(e.target.value)}
            required
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <PasswordInput margin="dense" setPassword={setPassword} inputProps={{ maxLength: 255 }} value={password} />
        </Grid>
      </>
    );
  }, [version, username, password, configData.stage, veevaUrl, docuListError]);

  const validateAccount = (str, setData) => {
    const isValid = regexAccountLocator(str);
    if (isValid) setData(str);
  };

  const SnowflakeFields = useCallback(() => {
    return (
      <>
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <TextField
            value={url}
            onChange={(e) => validateAccount(e.target.value, setURL)}
            fullWidth
            label="Account Locator"
            required
            disabled={configData.stage}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <TextField
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            fullWidth
            label="Integration Account"
            required
          />
        </Grid>
        <Grid size={6}>
          <FormControl>
            <FormLabel>Specify Authentication Method</FormLabel>
            <RadioGroup
              row
              value={authMethod}
              aria-labelledby="demo-controlled-radio-buttons-group-2"
              name="controlled-radio-buttons-group-2"
              onChange={(e) => {
                setAuthMethod((prevState) => {
                  prevState === "password" ? setPassword("") : setKey("");
                  return e.target.value;
                });
              }}
              className={classes.ifDisabled}
            >
              <FormControlLabel
                value="password"
                control={<Radio checked={authMethod === "password"} />}
                label="Password"
              />
              <FormControlLabel value="key" control={<Radio checked={authMethod === "key"} />} label="Key" />
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid
          sx={{ minHeight: "94px" }}
          size={{
            xs: 12,
            sm: 6
          }}>
          {authMethod === "password" ? (
            <PasswordInput margin="dense" setPassword={setPassword} inputProps={{ maxLength: 255 }} />
          ) : (
            <TextField
              onChange={(e) => setKey(e.target.value)}
              required
              fullWidth
              label="Private Key"
              multiline
              rows={2}
            />
          )}
        </Grid>
        <Grid p={0} size={12}>
          <Divider />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <TextField
            value={database}
            onChange={(e) => setPrevState(setSnowflakeState, { database: e.target.value })}
            fullWidth
            label="Database"
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <TextField
            value={schema}
            onChange={(e) => setPrevState(setSnowflakeState, { schema: e.target.value })}
            fullWidth
            label="Schema"
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <TextField
            value={warehouse}
            onChange={(e) => setPrevState(setSnowflakeState, { warehouse: e.target.value })}
            fullWidth
            label="Warehouse"
          />
        </Grid>
      </>
    );
  }, [authMethod, classes.ifDisabled, configData.stage, database, schema, url, username, warehouse]);

  return (
    <KMKDialog className={classes.dialogPaper} open={configData.open}>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar className={classes.Toolbar}>{configData.popupTitle} </Toolbar>
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "black",
          }}
        >
          <CloseIcon />
        </IconButton>
      </AppBar>
      <Box sx={{ width: "100%" }} className={classes.container}>
        <Backdrop sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="primary" />
        </Backdrop>
        <Box sx={{ width: "100%" }} component="form" className={classes.formContainer}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <FormControl fullWidth variant="outlined" sx={{ my: 2 }}>
                <InputLabel>Provider</InputLabel>
                <Select
                  value={dataProvider}
                  onChange={(e) => updateDataProvider(e.target.value)}
                  label="Provider"
                  className={classes.ifDisabled}
                  disabled={!!configData.stage}
                >
                  {configData.dataProviderList.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                required
                variant="outlined"
                error={!!dataIntegrationNameError}
                inputProps={{ maxLength: 15 }}
                helperText={dataIntegrationNameError}
                value={dataIntegrationName}
                onChange={(e) => configData.alphaWithUnderscore(e.target.value, setDataIntegrationName)}
                fullWidth
                label="Name"
                autoFocus
                disabled={!!configData.stage}
              />
            </Grid>
            {dataProvider && dataProvider.toLowerCase() === "salesforce" && (
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <TextField
                  required
                  variant="outlined"
                  inputProps={{ maxLength: 255 }}
                  value={url}
                  onChange={(e) => setURL(e.target.value)}
                  fullWidth
                  label="URL"
                  disabled={!!configData.stage}
                />
              </Grid>
            )}
            {dataProvider &&
              (() => {
                switch (dataProvider.toLowerCase()) {
                  case "s3":
                    return AWSS3Fields();
                  case "sftp":
                  case "mysql":
                    return SFTPFields();
                  case "salesforce":
                    return SalesforceFields();
                  case "sharepoint":
                    return SPFields();
                  case "veevavault":
                    return VeevaFields();
                  case "snowflake":
                    return SnowflakeFields();
                  default:
                    return AzureFields();
                }
              })()}
          </Grid>
          <DialogActions>
            <ButtonUI variant="contained" color="secondary" onClick={handleCancel}>
              Cancel
            </ButtonUI>
            <ButtonUI
              onClick={handleSave}
              variant="contained"
              color="success"
              style={{ marginLeft: 15 }}
              disabled={disabledSaveBtn()}
            >
              Save
            </ButtonUI>
          </DialogActions>
        </Box>
      </Box>
    </KMKDialog>
  );
}
